<script>
	import Layout from './layout.svelte';
	import TeamList from './game/team-list.svelte';

	import { serverTimestamp, serverTimeOffsetMillis, cleanAFK } from './services/firebase-service';

	import Clock from './components/clock.svelte';
	import GameSettings from './components/game-settings.svelte';

	// import Chat from './game/chat.svelte';

	import { STATUSES, TEAMS, TYPES, TIMER_LENGTHS } from './utils/constants';

	export let authID;
	export let gameID;
	export let game;
	export let gameRef;
	export let playersInput;
	export let authPlayerInput;

	$: host = window.location.host.toString() + '/';
	$: path = window.location.pathname.toString().replace('/', '');

	$: teamOne = playersInput.filter(p => p.requestedTeam === TEAMS.one);
	$: teamTwo = playersInput.filter(p => p.requestedTeam === TEAMS.two);

	$: numTeamOne = teamOne.length;
	$: numTeamTwo = teamTwo.length;
	$: totalPlayers = numTeamOne + numTeamTwo;

	$: notEnoughPlayers = (numTeamOne + numTeamTwo) < 4;
	$: notEnoughPlayersOnEachTeam = numTeamOne < 2 || numTeamTwo < 2;
	$: notEnough = notEnoughPlayers || notEnoughPlayersOnEachTeam;

	$: numPlayersClickedStart = [].concat(teamOne).concat(teamTwo).filter(p => p.clickedStart).length;
	$: authClickedStart = authPlayerInput.clickedStart;
	$: players = [].concat(teamOne).concat(teamTwo).map(p => ({
		...p,
		id: p.id,
		name: p.name,
		team: p.requestedTeam,
		isReadyToPlay: game.status < STATUSES.starting && p.clickedStart,
		isCurrent: p.id === authID
	}));

	$: isPubGame = game.type === TYPES.pub;
	$: isFullGame = !notEnough && totalPlayers >= game.numPlayersRequired;
	$: showTimer = isPubGame && isFullGame && players.some(p => p.clickedStart && p.clickedStartTimestamp);

	$: oldestClickedStart = players.filter(p => p.clickedStartTimestamp).sort((a, b) => a.clickedStartTimestamp.seconds - b.clickedStartTimestamp.seconds).shift();
	$: oldestClickedStartMillis = oldestClickedStart && (oldestClickedStart.clickedStartTimestamp.seconds * 1000 + oldestClickedStart.clickedStartTimestamp.nanoseconds / 1000000) || 0;

	let secondsRemaining;
	let interval;
	$: if (isPubGame && isFullGame && oldestClickedStartMillis) {
		updateSecondsRemaining();
		interval && clearInterval(interval);
		interval = setInterval(updateSecondsRemaining, 1000);
	}

	isPubGame && updateSecondsRemaining();
	function updateSecondsRemaining() {
		const serverTime = Date.now() + serverTimeOffsetMillis;
		const secondsToWait = 60;
		secondsRemaining = Math.max(0, secondsToWait - Math.round((serverTime - oldestClickedStartMillis) / 1000));
		if (secondsRemaining <= 0) {
			interval && clearInterval(interval);
			cleanAFK({ gameID });
		}
		else if (!isFullGame) {
			interval && clearInterval(interval);
		}
	}

	let previousTotalPlayers;
	$: if (totalPlayers !== previousTotalPlayers) {
		if (totalPlayers < previousTotalPlayers) {
			gameRef.collection('playersInput').doc(authID).update({ clickedStart: false, clickedStartTimestamp: null });
		}
		previousTotalPlayers = totalPlayers;
	}

	let copyText = 'copy';

	function submitStartGame(e) {
		gameRef.collection('playersInput').doc(authID).update({ clickedStart: true, clickedStartTimestamp: isFullGame ? serverTimestamp() : null });
	}

	function cancelStart(e) {
		gameRef.collection('playersInput').doc(authID).update({ clickedStart: false, clickedStartTimestamp: null });
	}

	function joinBlue() {
		gameRef.collection('playersInput').doc(authPlayerInput.id).update({ requestedTeam: TEAMS.one, clickedStart: false, clickedStartTimestamp: null });
	}
	function joinPink() {
		gameRef.collection('playersInput').doc(authPlayerInput.id).update({ requestedTeam: TEAMS.two, clickedStart: false, clickedStartTimestamp: null });
	}
	function leaveTeam() {
		gameRef.collection('playersInput').doc(authPlayerInput.id).update({ requestedTeam: null, clickedStart: false, clickedStartTimestamp: null });
	}

	function copyToClipboard(text) {
		navigator.clipboard.writeText(window.location.protocol + '//' + host + path).then(
			() => {
				copyText = 'copied!';
				setTimeout(() => copyText = 'copy', 5000);
			},
			(err) => {
				copyText = 'failed';
				setTimeout(() => copyText = 'copy', 5000);
				console.error('Async: Could not copy text: ', err);
			}
		);
	}
</script>

<Layout>
	<div class="timer" slot="clue" title="{secondsRemaining} seconds to click start">
		{#if showTimer}
			<Clock secondsRemaining={secondsRemaining} />
		{/if}
	</div>
	<form class="team-form team-one" slot="team-one">
		<TeamList {players} team="one" numPlayersRequired={game.numPlayersRequired || 4} />

		{#if game.status === STATUSES.created && game.type !== TYPES.pub}
			{#if authPlayerInput.requestedTeam !== TEAMS.one}
				<button
					on:click|preventDefault={joinBlue}
					on:touchstart|preventDefault={joinBlue}
				>
					Join Blue Team
				</button>
			{:else}
				<button
					class="leave-team"
					on:click|preventDefault={leaveTeam}
					on:touchstart|preventDefault={leaveTeam}
				>
					Leave Blue Team
				</button>
			{/if}
		{/if}
	</form>

	<div class="lobby" slot="core">
		<form
			class="start-game-form"
			on:submit|preventDefault={submitStartGame}>

			{#if game.type !== TYPES.pub}
				<p>
					<span class="share-text">Share this link <br/> to invite your friends:</span>
					<br />
					<span class="share-container">
						<span class="share-link"><span class="smaller">{host}</span>{path}</span>
						{#if navigator && navigator.clipboard}
							<button
								class="white-button copy-clipboard"
								on:click|stopPropagation|preventDefault={() => copyToClipboard()}
								on:touchstart|stopPropagation|preventDefault={() => copyToClipboard()}
							>
								{copyText}
							</button>
						{/if}
					</span>
				</p>
			{:else}
				<GameSettings {game} />
			{/if}

			<br />
			{#if notEnoughPlayers}
				<span>Waiting for players...</span>
			{:else if notEnoughPlayersOnEachTeam}
				<span>Waiting for at least two players on each team...</span>
			{:else}
				<span>Click start if you're ready</span>
			{/if}
			<br />

			<div class="button-group">
				{#if authClickedStart && (numPlayersClickedStart < totalPlayers || notEnough)}
					<button
						class="cancel"
						on:click|preventDefault|stopPropagation={cancelStart}
						on:touchstart|preventDefault|stopPropagation={cancelStart}
					>
						cancel
					</button>
				{/if}

				<button
					type="submit"
					class="start-game-submit"
					class:colorcycle={authClickedStart && numPlayersClickedStart >= totalPlayers && !notEnough}
					title="Start the game"
					disabled={notEnough || authClickedStart || !authPlayerInput.requestedTeam}
				>
					{#if !authClickedStart}
						Start
					{:else if notEnough || numPlayersClickedStart < totalPlayers}
						waiting for players...
					{:else}
						Starting!
					{/if}
				</button>
			</div>

			{#if numPlayersClickedStart}
				<div class="num-start">{numPlayersClickedStart} of {totalPlayers} players clicked start</div>
			{/if}
		</form>
	</div>

	<form class="team-form team-two" slot="team-two">
		<TeamList {players} team="two" numPlayersRequired={game.numPlayersRequired || 4} />

		{#if game.status === STATUSES.created && game.type !== TYPES.pub}
			{#if authPlayerInput.requestedTeam !== TEAMS.two}
				<button
					on:click|preventDefault={joinPink}
					on:touchstart|preventDefault={joinPink}
				>
					Join Pink Team
				</button>
			{:else}
				<button
					class="leave-team"
					on:click|preventDefault={leaveTeam}
					on:touchstart|preventDefault={leaveTeam}
				>
					Leave Pink Team
				</button>
			{/if}
		{/if}
	</form>

	<div
		class="message-container"
		class:one={authPlayerInput.requestedTeam === TEAMS.one}
		class:two={authPlayerInput.requestedTeam === TEAMS.two}
		slot="msg">
			{#if notEnough}
				<span>Waiting for players...</span>
			{:else}
				<span>Ready to start game...</span>
			{/if}
	</div>

	<!--
	<div
		class="chat-box"
		slot="chat">
		<Chat />
	</div>
-->
</Layout>


<style lang="less">
	@import "./variables.less";

	.timer {
		font-size: 2.5em;
	}

	.lobby {
		display: flex;
		width: 100%; height: 100%;
		padding: 0 1vmin;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background: rgba(255, 255, 255, 0.6);
		border: 3px solid #313131;
		border-radius: 20%;
		font-size: 1.8em;
		user-select: none; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none;

		.start-game-form {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			text-align: center;
			font-size: 2em;
			line-height: 1em;
			user-select: text; -webkit-touch-callout: unset; -webkit-user-select: text; -khtml-user-select: text; -moz-user-select: text; -ms-user-select: text;


			button {
				padding: 0.5em;

				&.cancel {
					color: white;
					background: #aaaaaa;
				}
			}

			.share-text {
				font-size: 1.4em;
			}

			.share-container {
				display: inline-block;
				padding: 0em 0.75em 0em 1em;
    			margin-top: 0.8em;
				color: white;
				background: #18d72e;
				border: 0.1em solid white;
				border-radius: 2.3rem;
				user-select: none; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none;
				box-shadow: 0 0 2em rgba(24, 215, 46, 0.5);
				line-height: 2em;

				.share-link {
					font-size: 0.8em;
					font-family: 'Courier New', Courier, Monaco, monospace, monospace;
					font-weight: 700;
					letter-spacing: -1px;
					user-select: text; -webkit-touch-callout: unset; -webkit-user-select: text; -khtml-user-select: text; -moz-user-select: text; -ms-user-select: text;
					text-shadow: 0 0 0.2em rgba(0, 0, 0, 0.3);

					.smaller {
						font-size: 0.8em;
						letter-spacing: 0;
						user-select: text; -webkit-touch-callout: unset; -webkit-user-select: text; -khtml-user-select: text; -moz-user-select: text; -ms-user-select: text;
					}
				}

				.copy-clipboard {
					margin-left: 0.6em;
					padding: 0.4em;
    				font-size: 0.5em;
					font-weight: 700;
					line-height: 1em;
				}
			}
		}

		.num-start {
			margin-top: 1em;
			padding: 0.5em;
			font-size: 0.8em;
			line-height: 1em;
			font-weight: 500;
			border: 0.1em solid white;
			border-radius: 0.5em;
			background: #18d72e;
			color: white;
		}
	}

	.team-form {
		display: inline-flex;
		width: 100%; max-width: 25em; height: 100%;
		padding: 0.75vmin 0; margin: 0 0.5em;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		font-size: 1.5em;
		text-align: center;

		@media (@aspect-ratio) {
			justify-content: flex-start;
		}

		button {
			margin: 0.75em 1em; padding: 0.7em 0.8em;
			font-weight: 700;
			font-size: 0.9em;

			&.leave-team {
				font-size: 0.5em;
				color: white;
				background: #aaaaaa !important;
			}
		}

		&.team-one {
			button {
				background: rgba(30,130,255,1);
			}
		}
		&.team-two {
			button {
				background: rgba(255,0,204,1);
				&.leave-team {
					font-size: 0.7em;
					color: white;
					background: #202020;
				}
			}
		}
	}

	.message-container {
		&.one {
			span {
				color: white;
				background-color: rgba(30,130,255,0.8);
			}
		}
		&.two {
			span {
				color: white;
				background-color: rgba(255,0,204, 0.8);
			}
		}

		span {
			display: inline-block;
			padding: 0.5em 1em;
			font-size: 2em;
			color: black;
			background: rgba(255,255,255,0.7);
			border: 0.2em solid rgba(255,255,255,1);
			border-bottom: 0;
			border-top: 0;
		}
	}

	.chat-box {
		width: 100%;
		height: 100%; max-height: 100%;
	}
</style>